'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import KioskHeader from './KioskHeader';
import KioskQRPanel from './KioskQRPanel';
import KioskStatusBar from './KioskStatusBar';
import KioskOfflineState from './KioskOfflineState';

const DEFAULT_TTL = 30;
const PROVISION_REQUEST_KEY = 'tei_kiosk_provision_request_id';
// Poll interval for activation check (ms)
const ACTIVATION_POLL_MS = 4000;

interface KioskTokenResponse {
  token: string;
  qrUrl: string;
  expiresAt: string;
  ttlSeconds: number;
  kioskName: string;
}

interface ProvisionState {
  requestId: string;
  displayCode: string;
  expiresAt: string;
}

export default function KioskDisplay() {
  const [currentToken, setCurrentToken] = useState<string>('');
  const [qrUrl, setQrUrl] = useState<string>('');
  const [secondsRemaining, setSecondsRemaining] = useState<number>(DEFAULT_TTL);
  const [ttlSeconds, setTtlSeconds] = useState<number>(DEFAULT_TTL);
  const [currentTime, setCurrentTime] = useState<string>('');
  const [currentDate, setCurrentDate] = useState<string>('');
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [tokenVersion, setTokenVersion] = useState<number>(0);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [kioskError, setKioskError] = useState<string | null>(null);

  // Provisioning state
  const [provisionState, setProvisionState] = useState<ProvisionState | null>(null);
  const [isRequestingProvision, setIsRequestingProvision] = useState<boolean>(false);
  const [provisionCodeExpired, setProvisionCodeExpired] = useState<boolean>(false);
  const [provisionSecondsLeft, setProvisionSecondsLeft] = useState<number>(0);

  const clockIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activationPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const provisionCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tokenExpiresAtRef = useRef<Date | null>(null);

  const formatTime = useCallback((date: Date): string => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const h = hours % 12 || 12;
    const m = String(minutes).padStart(2, '0');
    const s = String(seconds).padStart(2, '0');
    return `${h}:${m}:${s} ${ampm}`;
  }, []);

  const formatDate = useCallback((date: Date): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }, []);

  /**
   * Fetch a fresh QR token from the server.
   * The device credential is now an HttpOnly cookie — the browser sends it automatically.
   * No Authorization header or localStorage read needed.
   */
  const fetchToken = useCallback(async () => {
    setIsRefreshing(true);
    setKioskError(null);
    try {
      const res = await fetch('/api/kiosk/token', {
        method: 'GET',
        // credentials: 'include' is the default for same-origin requests in Next.js
        // The HttpOnly cookie is sent automatically by the browser
      });

      if (!res.ok) {
        const data = await res.json();
        setKioskError(data.error ?? 'Failed to get QR token');
        setIsAuthorized(false);
        setCurrentToken('');
        setQrUrl('');
        // If credential is revoked/invalid (403), clear the pending provision key
        // and start provisioning flow — the server will clear the cookie on next activate
        if (res.status === 403) {
          localStorage.removeItem(PROVISION_REQUEST_KEY);
        }
        return;
      }
      const data: KioskTokenResponse = await res.json();
      setCurrentToken(data.token);
      setQrUrl(data.qrUrl);
      setTtlSeconds(data.ttlSeconds);
      setSecondsRemaining(data.ttlSeconds);
      tokenExpiresAtRef.current = new Date(data.expiresAt);
      setIsAuthorized(true);
      setTokenVersion((v) => v + 1);
    } catch {
      setKioskError('Network error — cannot reach server');
      setIsAuthorized(false);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // Request a provisioning code from the server
  const requestProvision = useCallback(async () => {
    setIsRequestingProvision(true);
    setProvisionCodeExpired(false);
    try {
      const res = await fetch('/api/kiosk/request-provision', { method: 'POST' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setKioskError(data.error ?? 'Failed to request provisioning. Please refresh.');
        return;
      }
      const data = await res.json();
      const ps: ProvisionState = {
        requestId: data.requestId,
        displayCode: data.displayCode,
        expiresAt: data.expiresAt,
      };
      setProvisionState(ps);
      // Store only the requestId (not a credential) so we can resume after a page reload
      localStorage.setItem(PROVISION_REQUEST_KEY, data.requestId);

      // Countdown for display code expiry
      const expiresAt = new Date(data.expiresAt);
      if (provisionCountdownRef.current) clearInterval(provisionCountdownRef.current);
      provisionCountdownRef.current = setInterval(() => {
        const remaining = Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / 1000));
        setProvisionSecondsLeft(remaining);
        if (remaining <= 0) {
          setProvisionCodeExpired(true);
          if (provisionCountdownRef.current) clearInterval(provisionCountdownRef.current);
          if (activationPollRef.current) clearInterval(activationPollRef.current);
        }
      }, 1000);
      setProvisionSecondsLeft(Math.ceil((expiresAt.getTime() - Date.now()) / 1000));
    } catch {
      setKioskError('Network error requesting provisioning code.');
    } finally {
      setIsRequestingProvision(false);
    }
  }, []);

  /**
   * Poll for activation after provisioning code is shown.
   * On success the server sets the HttpOnly cookie — no localStorage write needed.
   */
  const pollActivation = useCallback(async (requestId: string) => {
    try {
      const res = await fetch('/api/kiosk/activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestId }),
      });
      if (!res.ok) return;
      const data = await res.json();

      if (data.activated) {
        // The server has set the HttpOnly cookie — no localStorage write needed
        localStorage.removeItem(PROVISION_REQUEST_KEY);
        if (activationPollRef.current) clearInterval(activationPollRef.current);
        if (provisionCountdownRef.current) clearInterval(provisionCountdownRef.current);
        setProvisionState(null);
        setProvisionCodeExpired(false);
        // Now fetch the first QR token (cookie is already set)
        await fetchToken();
      } else if (data.status === 'expired' || data.status === 'cancelled') {
        setProvisionCodeExpired(true);
        if (activationPollRef.current) clearInterval(activationPollRef.current);
      }
    } catch {
      // Network error during poll — will retry on next interval
    }
  }, [fetchToken]);

  /**
   * Initialize: try to fetch a QR token first (cookie may already be set from a previous session).
   * If the server returns 403, the cookie is absent/revoked — start provisioning flow.
   */
  useEffect(() => {
    const init = async () => {
      const now = new Date();
      setCurrentTime(formatTime(now));
      setCurrentDate(formatDate(now));

      // Attempt to get a QR token — the browser sends the HttpOnly cookie automatically
      setIsRefreshing(true);
      try {
        const res = await fetch('/api/kiosk/token', { method: 'GET' });
        if (res.ok) {
          const data: KioskTokenResponse = await res.json();
          setCurrentToken(data.token);
          setQrUrl(data.qrUrl);
          setTtlSeconds(data.ttlSeconds);
          setSecondsRemaining(data.ttlSeconds);
          tokenExpiresAtRef.current = new Date(data.expiresAt);
          setIsAuthorized(true);
          setTokenVersion((v) => v + 1);
          setIsRefreshing(false);
          return;
        }
      } catch {
        // Network error — fall through to provisioning
      }
      setIsRefreshing(false);

      // No valid cookie — check for a pending provision request from a previous session
      const savedRequestId = localStorage.getItem(PROVISION_REQUEST_KEY);
      if (savedRequestId) {
        const res = await fetch('/api/kiosk/activate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ requestId: savedRequestId }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.activated) {
            // Cookie set by server — fetch QR token
            localStorage.removeItem(PROVISION_REQUEST_KEY);
            await fetchToken();
            return;
          } else if (data.status === 'pending') {
            // Still pending — request a fresh code (we lost the displayCode on reload)
            localStorage.removeItem(PROVISION_REQUEST_KEY);
          }
        }
      }

      // No device cookie and no valid pending request — start provisioning
      await requestProvision();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start polling for activation once we have a provision state
  useEffect(() => {
    if (!provisionState || provisionCodeExpired) return;

    if (activationPollRef.current) clearInterval(activationPollRef.current);
    activationPollRef.current = setInterval(() => {
      pollActivation(provisionState.requestId);
    }, ACTIVATION_POLL_MS);

    return () => {
      if (activationPollRef.current) clearInterval(activationPollRef.current);
    };
  }, [provisionState, provisionCodeExpired, pollActivation]);

  // Live clock
  useEffect(() => {
    clockIntervalRef.current = setInterval(() => {
      const now = new Date();
      setCurrentTime(formatTime(now));
      setCurrentDate(formatDate(now));
    }, 1000);
    return () => { if (clockIntervalRef.current) clearInterval(clockIntervalRef.current); };
  }, [formatTime, formatDate]);

  // QR countdown — uses server expiry time for accuracy
  useEffect(() => {
    countdownIntervalRef.current = setInterval(() => {
      if (tokenExpiresAtRef.current) {
        const remaining = Math.max(0, Math.ceil((tokenExpiresAtRef.current.getTime() - Date.now()) / 1000));
        setSecondsRemaining(remaining);
        if (remaining <= 1) {
          fetchToken();
        }
      }
    }, 1000);
    return () => { if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current); };
  }, [fetchToken]);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => { setIsOnline(true); fetchToken(); };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [fetchToken]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (activationPollRef.current) clearInterval(activationPollRef.current);
      if (provisionCountdownRef.current) clearInterval(provisionCountdownRef.current);
    };
  }, []);

  const provisionMinutes = Math.floor(provisionSecondsLeft / 60);
  const provisionSecs = provisionSecondsLeft % 60;

  return (
    <div className="kiosk-bg min-h-screen w-full flex flex-col select-none overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(26,79,160,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(26,79,160,0.05) 0%, transparent 50%)',
        }}
      />

      <div className="relative z-10 flex flex-col min-h-screen">
        <KioskHeader
          currentTime={currentTime}
          currentDate={currentDate}
          isOnline={isOnline}
          isAuthorized={isAuthorized}
        />

        <main className="flex-1 flex items-center justify-center px-8 py-6">
          {!isOnline ? (
            <KioskOfflineState />
          ) : isAuthorized ? (
            <KioskQRPanel
              qrUrl={qrUrl}
              secondsRemaining={secondsRemaining}
              ttlSeconds={ttlSeconds}
              tokenVersion={tokenVersion}
              isRefreshing={isRefreshing}
            />
          ) : provisionState && !provisionCodeExpired ? (
            /* ── Provisioning Code Screen ── */
            <div className="flex flex-col items-center gap-8 max-w-lg text-center">
              <div className="w-20 h-20 rounded-full bg-blue-900 bg-opacity-30 flex items-center justify-center">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>

              <div>
                <p className="text-white text-2xl font-bold mb-1">Kiosk Not Authorized</p>
                <p className="kiosk-muted text-base">Show this code to your administrator to authorize this device.</p>
              </div>

              {/* Large display code */}
              <div className="kiosk-surface rounded-3xl px-12 py-8 border-2 kiosk-border" style={{ boxShadow: '0 0 40px rgba(26,79,160,0.25)' }}>
                <p className="kiosk-muted text-sm font-semibold uppercase tracking-widest mb-3">Provisioning Code</p>
                <p className="text-white font-mono font-bold tracking-[0.35em]" style={{ fontSize: '3.5rem', lineHeight: 1 }}>
                  {provisionState.displayCode}
                </p>
                <p className="kiosk-muted text-sm mt-4 tabular-nums">
                  Expires in{' '}
                  <span className={`font-bold ${provisionSecondsLeft <= 60 ? 'text-amber-400' : 'text-blue-400'}`}>
                    {String(provisionMinutes).padStart(2, '0')}:{String(provisionSecs).padStart(2, '0')}
                  </span>
                </p>
              </div>

              <div className="flex items-center gap-3 kiosk-muted text-sm">
                <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                <span>Waiting for administrator approval…</span>
              </div>

              <p className="kiosk-muted text-xs max-w-sm leading-relaxed">
                An administrator must sign into the admin dashboard from a separate device and approve this kiosk request.
              </p>
            </div>
          ) : provisionCodeExpired ? (
            /* ── Code Expired Screen ── */
            <div className="flex flex-col items-center gap-6 max-w-lg text-center">
              <div className="w-20 h-20 rounded-full bg-amber-900 bg-opacity-30 flex items-center justify-center">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <div>
                <p className="text-amber-400 text-2xl font-bold mb-2">Provisioning Code Expired</p>
                <p className="kiosk-muted text-base">The authorization code has expired. Generate a new one.</p>
              </div>
              <button
                onClick={requestProvision}
                disabled={isRequestingProvision}
                className="px-8 py-3 rounded-2xl font-bold text-white bg-blue-600 hover:bg-blue-500 transition-colors disabled:opacity-50"
              >
                {isRequestingProvision ? 'Generating…' : 'Generate New Code'}
              </button>
            </div>
          ) : (
            /* ── Generic Error / Revoked Screen ── */
            <div className="flex flex-col items-center gap-6 max-w-lg text-center">
              <div className="w-20 h-20 rounded-full bg-red-900 bg-opacity-30 flex items-center justify-center">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <div>
                <p className="text-red-400 text-2xl font-bold mb-2">Kiosk Not Authorized</p>
                <p className="kiosk-muted text-lg">{kioskError ?? 'This device is not authorized.'}</p>
                <p className="kiosk-muted text-sm mt-3">Contact your administrator to provision this device.</p>
              </div>
              {isRequestingProvision ? (
                <div className="flex items-center gap-3 kiosk-muted text-sm">
                  <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  <span>Requesting provisioning code…</span>
                </div>
              ) : (
                <button
                  onClick={requestProvision}
                  className="px-8 py-3 rounded-2xl font-bold text-white bg-blue-600 hover:bg-blue-500 transition-colors"
                >
                  Request Provisioning Code
                </button>
              )}
            </div>
          )}
        </main>

        <KioskStatusBar
          isOnline={isOnline}
          isAuthorized={isAuthorized}
          secondsRemaining={secondsRemaining}
        />
      </div>
    </div>
  );
}